import * as THREE from 'three'

declare module 'three' {
  interface Object3DEventMap {
    'realismTraffic-loaded': { }
  }
  interface Object3D {
    _sceneInstance?: any
  }
  interface EventDispatcher {
    addEventListener(type: string, listener: (event: any) => void): void
    removeEventListener(type: string, listener: (event: any) => void): void
    dispatchEvent(event: { type: string; [key: string]: any }): void
    __ANOV_EXTENDED__: boolean
  }
}
export const installObjectExtension = () => {
  if ((THREE.Object3D.prototype as any).__ANOV_EXTENDED__)
    return
  const originalAddEventListener = THREE.Object3D.prototype.addEventListener
  const originalRemoveEventListener = THREE.Object3D.prototype.removeEventListener
  const onBeforeRender = THREE.Object3D.prototype.onBeforeRender

  // @ts-ignore
  const onBeforeRenderMaterial = THREE.Material.prototype.onBeforeRender

  // @ts-ignore
  const onBuildMaterial = THREE.Material.prototype.onBuild

  THREE.Object3D.prototype.onBeforeRender = function (...args) {
    const warn = console.warn

    console.warn = () => { }
    onBeforeRender.call(this, ...args)
    console.warn = warn
  }

  // @ts-ignore
  THREE.Material.prototype.onBeforeRender = function (...args) {
    const warn = console.warn

    console.warn = () => { }
    onBeforeRenderMaterial.call(this, ...args)
    console.warn = warn
  }

  // @ts-ignore
  THREE.Material.prototype.onBuild = function (...args) {
    const warn = console.warn

    console.warn = () => { }
    onBuildMaterial.call(this, ...args)
    console.warn = warn
  }

  const events = ['click', 'dblclick', 'contextmenu', 'mousedown', 'mouseup', 'mouseover', 'mouseout', 'mousemove']

  /**
   * 重写 addEventListener
   * 1. 事件类型检查
   * 2. 初始化 userData
   * 3. 添加 listener
   * 4. 注册事件类型
   */
  THREE.Object3D.prototype.addEventListener = function (type: string, listener: any) {
    if (events.includes(type)) {
      if (!this.userData.eventHashSet)
        this.userData.eventHashSet = new Set()

      if (!this.userData.listenerTracker)
        this.userData.listenerTracker = {}

      if (!this.userData.listenerTracker[type])
        this.userData.listenerTracker[type] = new Set()

      this.userData.listenerTracker[type].add(listener)

      // 只有当该类型的监听器数量为 1 时（即第一个），才进行注册逻辑
      if (this.userData.listenerTracker[type].size === 1) {
        const wasEmpty = this.userData.eventHashSet.size === 0
        this.userData.eventHashSet.add(type)

        // 关键改进:如果对象已在场景中,立即注册到 interactionManager
        if (this.parent && this._sceneInstance) {
          const sceneInstance = this._sceneInstance
          if (sceneInstance.interactionManager) {
            sceneInstance.interactionManager.registerEventType(type)

            // 如果这是第一个事件,需要 add 对象;否则只需更新事件类型
            if (wasEmpty) {
              sceneInstance.interactionManager.add(this, [type])
            }
            else {
              // 更新已存在对象的事件类型
              sceneInstance.interactionManager.add(this, Array.from(this.userData.eventHashSet))
            }
          }
        }
      }
    }

    originalAddEventListener.call(this, type, listener as any)
  }

  THREE.Object3D.prototype.removeEventListener = function (type: string, listener: any) {
    originalRemoveEventListener.call(this, type, listener as any)

    if (events.includes(type) && this.userData.eventHashSet) {
      if (this.userData.listenerTracker && this.userData.listenerTracker[type]) {
        this.userData.listenerTracker[type].delete(listener)

        // 只有当该类型的监听器数量归零时，才执行移除逻辑
        if (this.userData.listenerTracker[type].size === 0) {
          this.userData.eventHashSet.delete(type)
          delete this.userData.listenerTracker[type]

          // 关键改进:从 interactionManager 中移除
          if (this.parent && this._sceneInstance) {
            const sceneInstance = this._sceneInstance
            if (sceneInstance.interactionManager) {
              sceneInstance.interactionManager.unregisterEventType(type)

              // 如果没有其他交互事件了,完全移除对象
              if (this.userData.eventHashSet.size === 0) {
                sceneInstance.interactionManager.remove(this)
              }
              else {
                // 否则更新事件类型列表
                sceneInstance.interactionManager.add(this, Array.from(this.userData.eventHashSet))
              }
            }
          }
        }
      }
    }
  }
  /**
   * 重写 add
   * 当对象已在场景树中（拥有 _sceneInstance），向其添加子对象时，
   * 自动为新子对象传播 _sceneInstance 并注册事件到 interactionManager。
   * 解决通过 group.add(child) 添加对象后事件无法触发的问题。
   */
  const originalAdd = THREE.Object3D.prototype.add
  THREE.Object3D.prototype.add = function (...objects: THREE.Object3D[]) {
    const result = originalAdd.call(this, ...objects)

    if (this._sceneInstance) {
      const sceneInstance = this._sceneInstance
      objects.forEach((obj) => {
        if (obj && obj.traverse) {
          obj.traverse((child: any) => {
            child._sceneInstance = sceneInstance

            if (child.userData?.eventHashSet?.size > 0 && sceneInstance.interactionManager) {
              child.userData.eventHashSet.forEach((type: string) => {
                sceneInstance.interactionManager.registerEventType(type)
              })
              sceneInstance.interactionManager.add(child, Array.from(child.userData.eventHashSet))
            }
          })
        }
      })
    }

    return result
  }

  /**
   * 重写 remove
   * 当对象已在场景树中，从其移除子对象时，
   * 自动从 interactionManager 中注销对应事件。
   */
  const originalRemoveObj = THREE.Object3D.prototype.remove
  THREE.Object3D.prototype.remove = function (...objects: THREE.Object3D[]) {
    // 在移除前清理事件注册
    if (this._sceneInstance) {
      const sceneInstance = this._sceneInstance
      objects.forEach((obj) => {
        if (obj && obj.traverse && sceneInstance.interactionManager) {
          obj.traverse((child: any) => {
            if (child.userData?.eventHashSet?.size > 0) {
              sceneInstance.interactionManager.remove(child)
            }
          })
        }
      })
    }

    return originalRemoveObj.call(this, ...objects)
  }

  THREE.Object3D.prototype.__ANOV_EXTENDED__ = true
}
